/**
 * Level 4 — the Abandoned Office.
 *
 * "White, concrete-like walls and cheap carpet. The carpet is similar to Level
 * 0's, but it is grey." The wiki's other load-bearing line is about the light:
 * *most of the ceiling lights do not work, and the level is lit by the constant
 * light from the windows — most of which are blacked out yet still emit light.*
 * That is the reason to build this level at all. Every other floor in the game
 * is lit by fixtures, so a bright floor with almost every fixture dead reads as
 * a completely different place even before you look at the walls.
 *
 * The plan is a real office floor plate rather than the Lobby's endless
 * partitions: a band of cellular offices against the glass, a ring corridor
 * behind them, a service core in the middle, and open plate either side of it
 * filled with a cube farm on a fixed pitch. It is the most legible level in the
 * game up close and no help at all at range, because a ring corridor looks the
 * same on all four sides and the core has no front.
 *
 * Survival difficulty 1, and Almond Water in the coolers and the vending
 * machines. This is where a run gets its breath back.
 */

import { Cell } from '../../core/types';
import { scatterDamp } from './decorate';
import {
  addRegion,
  fillRect,
  inset,
  interiorRect,
  type GenSite,
  type Rect,
} from './site';
import { applyWrongness } from './wrongness';

/** Ring corridor width in cells. */
const RING = 3;
/** Cubicle pitch: dividers this many cells apart. */
const CUBE_PITCH = 3;
/** Cells of clear floor left at each end of a divider run, so a plate never seals. */
const CUBE_MARGIN = 2;
/** Smallest plate that fits a core, a corridor either side of it and a pocket. */
const MIN_PLATE = 26;

/** Carve the Abandoned Office profile into `site`. */
export function generateOffices(site: GenSite): void {
  const { grid, rng } = site;
  const outer = inset(interiorRect(grid), 1);
  const band = rng.int(5, 7);
  // The plate has to hold the core, a two-cell corridor either side of it and a
  // pocket beyond that. Below this the plan cannot be drawn at all, so the level
  // falls back to one open floor rather than to a grid of solid rock.
  if (outer.w < 2 * (band + RING) + MIN_PLATE || outer.h < 2 * (band + RING) + MIN_PLATE) {
    fillRect(grid, outer, Cell.Floor);
    addRegion(site, outer, 'atrium');
    return;
  }

  // The ring corridor: a loop of open floor between the window offices and the
  // plate. Carved first so everything else can punch a door onto it.
  const ringOuter = inset(outer, band);
  const plate = inset(ringOuter, RING);
  fillRect(grid, ringOuter, Cell.Floor);
  fillRect(grid, plate, Cell.Wall);
  addRegion(site, { x: ringOuter.x, y: ringOuter.y, w: ringOuter.w, h: RING }, 'hall');
  addRegion(site, { x: ringOuter.x, y: ringOuter.y + ringOuter.h - RING, w: ringOuter.w, h: RING }, 'hall');
  addRegion(site, { x: ringOuter.x, y: ringOuter.y, w: RING, h: ringOuter.h }, 'hall');
  addRegion(site, { x: ringOuter.x + ringOuter.w - RING, y: ringOuter.y, w: RING, h: ringOuter.h }, 'hall');

  carveWindowOffices(site, outer, ringOuter);
  const core = carveCore(site, plate);
  const quadrants = carveCrossCorridors(site, plate, core);
  for (const q of quadrants) carveCubeFarm(site, q);

  // Office fit-out gone wrong is the register here: a partition that stops, a
  // meeting room built inside a meeting room, a doorway into the riser. The
  // inner-window operation is the one that matters most on this level — the
  // canon detail is a window you cannot see out of, and an interior window into
  // a room with nothing lit in it is exactly that object.
  applyWrongness(site, {
    innerRooms: [6, 10],
    doorsToNothing: [8, 14],
    perforatedWalls: [1, 3],
    pillarsInDoorways: [1, 3],
    wallFins: [6, 12],
    tightPillarPatches: [1, 3],
    // A ring corridor around the plate is already the level's shape; a second
    // one carved by the inner-room operation would read as the plan repeating.
    ringRooms: false,
  });

  // The one thing the wiki says is different about this carpet: it is grey and
  // there is no carpet fluid in it. So the damp is sparse and it is all around
  // the coolers, not seeping out of the walls.
  scatterDamp(site, rng.int(4, 9), 3, 10);
}

/**
 * The cellular offices against the glass. Each opens onto the ring corridor and
 * backs onto the outer wall, which on this level is the window wall: the light
 * in these rooms comes from behind you and there is nothing to see through it.
 */
function carveWindowOffices(site: GenSite, outer: Rect, ringOuter: Rect): void {
  const { grid, rng } = site;
  // One entry per side of the plate. `a`..`b` runs along the band, `roomA`..
  // `roomB` is its depth, and `doorLine` is the wall it shares with the ring —
  // always the edge of the band nearer the middle of the building. Corners are
  // left solid, which is where a real building puts its stairs.
  const outerX1 = outer.x + outer.w - 1;
  const outerY1 = outer.y + outer.h - 1;
  const ringX1 = ringOuter.x + ringOuter.w - 1;
  const ringY1 = ringOuter.y + ringOuter.h - 1;
  const sides: Array<{
    along: 'x' | 'y';
    a: number;
    b: number;
    roomA: number;
    roomB: number;
    doorLine: number;
  }> = [
    { along: 'x', a: ringOuter.x, b: ringX1, roomA: outer.y, roomB: ringOuter.y - 2, doorLine: ringOuter.y - 1 },
    { along: 'x', a: ringOuter.x, b: ringX1, roomA: ringY1 + 2, roomB: outerY1, doorLine: ringY1 + 1 },
    { along: 'y', a: ringOuter.y, b: ringY1, roomA: outer.x, roomB: ringOuter.x - 2, doorLine: ringOuter.x - 1 },
    { along: 'y', a: ringOuter.y, b: ringY1, roomA: ringX1 + 2, roomB: outerX1, doorLine: ringX1 + 1 },
  ];

  for (const side of sides) {
    if (side.roomB - side.roomA + 1 < 2) continue;
    let t = side.a + 1;
    while (t < side.b) {
      let width = rng.int(4, 8);
      if (t + width > side.b) width = side.b - t;
      if (width >= 2) {
        const r: Rect =
          side.along === 'x'
            ? { x: t, y: side.roomA, w: width, h: side.roomB - side.roomA + 1 }
            : { x: side.roomA, y: t, w: side.roomB - side.roomA + 1, h: width };
        fillRect(grid, r, Cell.Floor);
        const d = t + rng.int(0, width - 1);
        if (side.along === 'x') grid.set(d, side.doorLine, Cell.Doorway);
        else grid.set(side.doorLine, d, Cell.Doorway);
        addRegion(site, r, r.w * r.h <= 14 ? 'closet' : 'room');
      }
      t += (width >= 2 ? width : 1) + 1;
    }
  }
}

/**
 * The service core: lifts and risers, sealed, with a lift lobby cut into one
 * face of it. Every office building has one and nobody who works in one could
 * describe it, which is why it belongs here — it is the only landmark on the
 * plate and it looks the same from three sides.
 */
function carveCore(site: GenSite, plate: Rect): Rect {
  const { grid, rng } = site;
  const w = rng.int(9, 13);
  const h = rng.int(7, 10);
  const core: Rect = {
    x: plate.x + ((plate.w - w) >> 1),
    y: plate.y + ((plate.h - h) >> 1),
    w,
    h,
  };
  fillRect(grid, core, Cell.Wall);

  // The lift lobby: a room inside the core, open to one side only.
  const lobbyW = Math.max(3, w - 4);
  const lobbyH = 2;
  const onTop = rng.chance(0.5);
  const lobby: Rect = {
    x: core.x + ((w - lobbyW) >> 1),
    y: onTop ? core.y + 1 : core.y + h - 1 - lobbyH,
    w: lobbyW,
    h: lobbyH,
  };
  fillRect(grid, lobby, Cell.Floor);
  for (let k = 1; k < lobbyW - 1; k++) {
    grid.set(lobby.x + k, onTop ? core.y : core.y + h - 1, Cell.Doorway);
  }
  addRegion(site, lobby, 'room');
  return core;
}

/**
 * Two corridors through the plate, passing either side of the core, meeting the
 * ring at both ends. Returns the open plates left between them.
 */
function carveCrossCorridors(site: GenSite, plate: Rect, core: Rect): Rect[] {
  const { grid } = site;
  // The corridors hug the core on all four faces, so the lift lobby's opening
  // lands on one of them whichever way `carveCore` turned it.
  const left = core.x - 2;
  const top = core.y - 2;
  const right = core.x + core.w;
  const bottom = core.y + core.h;

  const runs: Rect[] = [
    { x: plate.x, y: top, w: plate.w, h: 2 },
    { x: plate.x, y: bottom, w: plate.w, h: 2 },
    { x: left, y: plate.y, w: 2, h: plate.h },
    { x: right, y: plate.y, w: 2, h: plate.h },
  ];
  for (const run of runs) {
    fillRect(grid, run, Cell.Floor);
    addRegion(site, run, 'hall');
  }

  // The eight pockets the two pairs of corridors cut the plate into. The four
  // corner ones are the cube farms; the four beside the core are shallower and
  // get the same treatment, which is what makes the plate read as one floor.
  const xs: Array<[number, number]> = [
    [plate.x, left - 1],
    [core.x, core.x + core.w - 1],
    [right + 2, plate.x + plate.w - 1],
  ];
  const ys: Array<[number, number]> = [
    [plate.y, top - 1],
    [core.y, core.y + core.h - 1],
    [bottom + 2, plate.y + plate.h - 1],
  ];

  const out: Rect[] = [];
  for (let j = 0; j < ys.length; j++) {
    for (let i = 0; i < xs.length; i++) {
      // The middle pocket is the core itself.
      if (i === 1 && j === 1) continue;
      const [x0, x1] = xs[i]!;
      const [y0, y1] = ys[j]!;
      if (x1 - x0 + 1 < 4 || y1 - y0 + 1 < 4) continue;
      out.push({ x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 });
    }
  }
  return out;
}

/**
 * Open plate with a cube farm stamped on it.
 *
 * The dividers are full-height here rather than shoulder-height, because a
 * shoulder-height wall is not something this grid can express — and a cube farm
 * you cannot see over is a better version of the same idea anyway. Every run
 * stops short of both ends of the plate, so a lap of the pocket is always
 * possible and the pass can never seal anything off.
 */
function carveCubeFarm(site: GenSite, plate: Rect): void {
  const { grid, rng } = site;
  fillRect(grid, plate, Cell.Floor);
  addRegion(site, plate, plate.w * plate.h > 400 ? 'atrium' : 'room');

  const inner = inset(plate, CUBE_MARGIN);
  if (inner.w < 4 || inner.h < 4) return;
  const horizontal = plate.w >= plate.h;
  const laneSpan = horizontal ? inner.h : inner.w;
  const laneOrigin = horizontal ? inner.y : inner.x;
  const runOrigin = horizontal ? inner.x : inner.y;
  const runSpan = horizontal ? inner.w : inner.h;

  for (let lane = laneOrigin; lane < laneOrigin + laneSpan; lane += CUBE_PITCH) {
    // The occasional missing run is what stops the farm reading as a comb, and
    // it is the only thing on the plate a player can navigate by.
    if (rng.chance(0.18)) continue;
    for (let k = 0; k < runSpan; k++) {
      const t = runOrigin + k;
      if (horizontal) grid.set(t, lane, Cell.Wall);
      else grid.set(lane, t, Cell.Wall);
    }
    // Cross dividers turn the run into cubicles rather than a fence. They stop
    // one cell short of the next run so the aisle behind is always open.
    let t = runOrigin + rng.int(1, CUBE_PITCH);
    while (t < runOrigin + runSpan) {
      for (let k = 1; k < CUBE_PITCH - 1; k++) {
        if (horizontal) grid.set(t, lane + k, Cell.Wall);
        else grid.set(lane + k, t, Cell.Wall);
      }
      t += rng.int(2, 4);
    }
  }
}
